
import { Injectable } from '@angular/core';
import * as models from '../../shared/model/api/DealerFeature';
import { Observable, BehaviorSubject } from 'rxjs';
import { isNil } from 'lodash';
import * as _lodash from 'lodash-es';
import { Store } from '@ngrx/store';
import * as fromSelectors from '../state/selectors/index';
import { AppState } from '../../state/app.state';
import { Logger } from './logger';
import { SharedDataService } from './shared-data.service';
import { ViewConstants} from '../../constants';
import { EnvironmentConfiguration } from '../../environment/services/environment.configuration';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, catchError, tap } from 'rxjs/operators';

@Injectable({ providedIn: "root" })
export class AutorizationService {
  // variable to hold the auth data
  private dealerFeatures$: Observable<models.DealerFeature[]>;
  private dealerFeaturesSubscriber: any;
  private availableDealerFeatures: models.DealerFeature[] = null;
  protected basePath = this.environment.MST_BASE_SERVICE_URL;
  protected rtiBasePath = this.environment.BASE_SERVICE_URL;
  public brand: string;
  /**
   * Constructor for dealer feature access service
   */
  constructor(
    private http: HttpClient,
    private store: Store<AppState>,
    private logger: Logger,
    private sharedDataService: SharedDataService,
    private environment: EnvironmentConfiguration
  ) {
    this.dealerFeatures$ = store.select(fromSelectors.getAuthAccess);
    this.getBrand().subscribe((brand) => {
      if (!isNil(brand)) {
        this.brand =
          brand.toLocaleLowerCase().charAt(0).toUpperCase() + brand.slice(1); //Capitalize the first letter
      }
    });
  }

  /**
   * Method to be set the dealer features
   */
  setDealerFeatures(dealerFeatures: models.DealerFeature[]) {
    this.availableDealerFeatures = dealerFeatures;
  }
  /**
   * Method to be provide if a dealer has access to a feature or not
   */
  hasFeatureAccess(featureName: string): boolean {
    let hasAccess: boolean = false;
    if (this.getDealerFeatureIndex(featureName) > -1) {
      hasAccess = true;
    }
    return hasAccess;
  }
  /**
   * Method to provide the feature details
   */
  getDealerFeatureDetails(featureName: string): models.DealerFeature {
    let dealerFeature: models.DealerFeature = null;
    let dealerFeatureIndex: number = -1;
    dealerFeatureIndex = this.getDealerFeatureIndex(featureName);
    if (dealerFeatureIndex > -1) {
      dealerFeature = this.availableDealerFeatures[dealerFeatureIndex];
    }
    this.turnOffSubscriber();
    return dealerFeature;
  }

  /**
   * Method to get dealer feature index
   */
  private getDealerFeatureIndex(featureName: string) {
    let dealerFeatureIndex: number = -1;
    if (!isNil(this.availableDealerFeatures)) {
      dealerFeatureIndex = this.availableDealerFeatures.findIndex(
        (dt) =>
          !isNil(dt.masterFeature) &&
          dt.masterFeature.name === featureName &&
          dt.masterFeature.active === true &&
          dt.active === true
      );
    }
    return dealerFeatureIndex;
  }
  /**
   * Method to turn off subscriber
   */
  private turnOffSubscriber() {
    if (this.dealerFeaturesSubscriber) {
      this.dealerFeaturesSubscriber.unsubscribe();
    }
  }

  public getDealerDetails(
    brandId: string,
    dealerCode: string,
    searchMode: string,
    resultsMode: string,
    resultsFormat: string,
    appKey: string
  ): Observable<any> {
    this.logger.log("Executing Get dealer details method");
    const path = this.basePath + "dealers/" + dealerCode;
    if (!dealerCode) {
      let msg = ViewConstants.REQUIRED_PARAMETER_IS_NULL_OR_UNDEFINED.replace(
        "{ServiceName}",
        "Get Dealer Detail"
      ).replace("{ParameterName}", "dealerCode");
      return this.sharedDataService.handleError(msg);
    }
    const options = { params: new HttpParams().set("source", "CON") };
    return this.http.get(path, options).pipe(
      map((o) => this.getDealerDetailsResponse(o)),
      catchError((err) => this.sharedDataService.handleError(err))
    );
  }
  public getShortenLinkForPP(dealerType: any, dealerBaseUrl: any): Observable<any> {
    const payload = {
      dealerType: dealerType,
      dealerBaseUrl: dealerBaseUrl
    }
    const path = this.environment.MST_BASE_SERVICE_URL + `user/${this.sharedDataService.dealerCode}/share/?shortUrl=true&type=share-pp`;
    return this.http.post(path, payload).pipe(
      map((response) => response),
      catchError((err) => this.sharedDataService.handleError(err))
    );
  }
  public getCredit(regionCode): Observable<any> {
    const path = `${this.basePath}payment-nexgen/credittiers/${regionCode}?vehicleType=new`;
    return this.http.get(path).pipe(
      map((response) => response),
      catchError((err) => this.sharedDataService.handleError(err))
    );
  }
  public getUserPreferences(payload): Observable<any> {
    const path = this.environment.MST_BASE_SERVICE_URL + `user/${payload.dealerCode}/preferences/${payload.userID}`;
    return this.http.get(path).pipe(
      map((views) => views["response"][0]),
      catchError((err) => this.sharedDataService.handleError(err))
    );
  }
  public getSalesConsultatnt(payload): Observable<any> {
    const path = this.environment.MST_BASE_SERVICE_URL + `dealers/${payload.dealerCode}` + "/user?role=DD_NewVehicleVMSExcludeCost";
    return this.http.get(path).pipe(
      map((sales) => sales),
      catchError((err) => this.sharedDataService.handleError(err))
    );
  }

  public updateCustomView(payload): Observable<any> {
    const path = this.environment.MST_BASE_SERVICE_URL + `user/${payload.dealerCode}/preferences/${payload.userID}?views=true`;
    return this.http.post(path, payload.views)
  }

  public updateMyTeams(payload): Observable<any> {
    const path = this.environment.MST_BASE_SERVICE_URL + `user/${payload.dealerCode}/preferences/${payload.userID}?teams=true`;
    return this.http.post(path, payload.views);
  }

  public updateTheTheme(payload): Observable<any> {
    const path = this.environment.MST_BASE_SERVICE_URL + `user/${this.sharedDataService.dealerCode}/preferences/${this.sharedDataService.loggedInUserId}?darkMode=true`;
    return this.http.post(path, {darkTheme: payload.darkTheme}).pipe(
      map((response) => response),
      catchError((err) => this.sharedDataService.handleError(err))
    );
  }
  public updateLogView(payload): Observable<any> {
    const path =
      this.environment.MST_BASE_SERVICE_URL +
      `user/${payload.dealerCode}/preferences/${payload.userID}?logView=true`;
    return this.http.post(path, payload.logView);
  }
  /**
   * get the getDealerConfigDetails for lease write-ups
   */
  public getDealerConfigDetails(dealerCode: string): Observable<any> {
    // const path = this.rtiBasePath + "/feature-mgmt-consumer-service/" + dealerCode + "/dealer-features";
    const path = this.rtiBasePath + "/feature-config/" + dealerCode + "/dealer-features";
    if (!dealerCode) {
      let msg = ViewConstants.REQUIRED_PARAMETER_IS_NULL_OR_UNDEFINED.replace(
        "{ServiceName}",
        "Get Dealer Detail"
      ).replace("{ParameterName}", "dealerCode");
      return this.sharedDataService.handleError(msg);
    }
    return this.http.get(path).pipe(
      map((o) => this.getDealerConfigFlag(o)),
      catchError((err) => this.sharedDataService.handleError(err))
    );
  }
  /**
   * MethodName : getDealerConfigFlag
   * Get the lease value of 10k miles
   */
  public getDealerConfigFlag(response) {
    let defaultValue = false;
    let originalResp = [...response];
    this.sharedDataService.loadPEVersion = true;
    this.loadPEVersion(originalResp);
    response = response.filter(
      (dt) =>
        dt &&
        dt.masterFeature &&
        dt.masterFeature.name.toLowerCase() === ViewConstants.MST_FEATURES.toLowerCase()
    )[0];
    function processAttribute(response, attributeName, isBoolean, action) {
      if (response && !isNil(response.overridenFeatureAttributes) && response.overridenFeatureAttributes.length > 0) {
        for (const attribute of response.overridenFeatureAttributes) {
          if (!isNil(attribute) && !isNil(attribute.masterFeatureAttribute) && attribute.masterFeatureAttribute.name === attributeName && !isNil(attribute.value)) {
            if (isBoolean) {
              return attribute.value === "true";
            } else {
              return attribute.value;
            }
          }
        }
      }
      if (response && !isNil(response.masterFeature) && !isNil(response.masterFeature.attributes)) {
        for (const attribute of response.masterFeature.attributes) {
          if (!isNil(attribute) && attribute.name === attributeName && !isNil(attribute.defaultValue)) {
            if (!action) {
              if (isBoolean) {
                return attribute.defaultValue === "true";
              } else {
                return attribute.defaultValue;
              }
            } else {
              return action(attribute);
            }
          }
        }
      }
    }
    const sharedDataKeys = [
      { key: 'isDFIVisible', attr: ViewConstants.DFI, isBoolean: true },
      { key: 'isBusinessOCA', attr: ViewConstants.Business_OCA, isBoolean: true },
      { key: 'isDealJacketVisible', attr: ViewConstants.Send_to_Deal_Jacket, isBoolean: true },
      { key: 'isDeskDealVisible', attr: ViewConstants.Desk_Deal, isBoolean: true },
      { key: 'isFIAdminUI', attr: ViewConstants.FI_Admin_UI, isBoolean: true },
      { key: 'protectionProduct', attr: ViewConstants.PROCTECTION_PRODUCTS, isBoolean: true },
      { key: 'isDesking', attr: ViewConstants.Desking, isBoolean: true },
      { key: 'zeroDueSigning', attr: ViewConstants.Zero_Due_at_Signing, isBoolean: true },
      { key: 'firstDueSigning', attr: ViewConstants.First_Payment_Due_at_Signing, isBoolean: true },
      { key: 'feaureDefaultValue', attr: ViewConstants.Payment_Grid_order, isBoolean: false, defaultValue: `${ViewConstants.DEAL_TYPE_FINANCE[0].toUpperCase()}${ViewConstants.DEAL_TYPE_FINANCE.substring(1)}` },
      { key: 'rebateCapConfig', attr: ViewConstants.Rebate_Capitalization, isBoolean: false },
      { key: 'tradeCapConfig', attr: ViewConstants.Net_Trade_in_Capitalization, isBoolean: false },
      { key: 'cashdownCapConfig', attr: ViewConstants.Cashdown_Capitalization, isBoolean: false },
      { key: 'isUVIEndpoint', attr: ViewConstants.UVI_endpoint, isBoolean: true, defaultValue: true },
      { key: 'smartPathDealer', attr: ViewConstants.Smartpath_Dealer, isBoolean: true },
      { key: 'residualOverride', attr: ViewConstants.Residual_Override, isBoolean: true },
      { key: 'leaseVariant', attr: ViewConstants.Lease_Variant, isBoolean: true },
      { key: 'onePayLease', attr: ViewConstants.One_Pay_Lease, isBoolean: true },
      { key: 'isShareEnabled', attr: ViewConstants.Enable_Share_Features, isBoolean: true },
      { key: 'isOCAEnabled', attr: ViewConstants.OCA, isBoolean: true },
      { key: 'isSpecialRateEnabled', attr: ViewConstants.Special_Rate_Toggle, isBoolean: true },
      { key: 'isShare2OnlineAccountEnabled', attr: ViewConstants.Enable_Share_to_Online_Account, isBoolean: true },
      { key: 'isTcuvEnabled', attr: ViewConstants.TCUV, isBoolean: true, defaultValue: true },
      { key: 'defaultSP', attr: ViewConstants.LEAD_TYPE_VEHICLE_SELLING_PRICE, isBoolean: false, defaultValue: '' },
      { key: 'sendToTransactionsConfig', attr: ViewConstants.Send_to_Transactions, isBoolean: true }
    ];
    sharedDataKeys.forEach(({ key, attr, isBoolean, defaultValue }) => {
      this.sharedDataService[key] = processAttribute(response, attr, isBoolean, null) ? processAttribute(response, attr, isBoolean, null) : (defaultValue !== undefined ? defaultValue : false);
    });
    this.sharedDataService.dealerConfig = defaultValue;
    defaultValue = processAttribute(response, ViewConstants.Low_Mileage_Lease, true, null) ? processAttribute(response, ViewConstants.Low_Mileage_Lease, true, null) : false;
    this.sharedDataService.nonToyotaProviderId = processAttribute(response, ViewConstants.NonToyotaProviderId, false, (dt) => {
      return dt.defaultValue.toLowerCase().split(",") ? dt.defaultValue.toLowerCase().split(",") : [];
    });
    this.sharedDataService.leaseMilageNew = processAttribute(response, ViewConstants.Lease_Mileage_New, false, (dt) => {
      return Number(dt.defaultValue) ? Number(dt.defaultValue) : 0;
    });
    this.sharedDataService.leaseMilageUsed = processAttribute(response, ViewConstants.Lease_Mileage_Certified, false, (dt) => {
      return Number(dt.defaultValue) ? Number(dt.defaultValue) : 0;
    });
    return defaultValue;
  }

  loadPEVersion = (originalResp) => {
    if (originalResp.some(dt =>
      dt && dt.masterFeature &&
      dt.masterFeature.name.toLowerCase() === "usepaymentengine2" && !dt.active
    )) {
      this.sharedDataService.loadPEVersion = false;
    }
  }

  //
  // Method to Call the User Info Service
  //
  public loadUserInfo() {
    const path = this.basePath + "user/userInfo";
    return this.http.get(path).pipe(
      map((response) => response),
      catchError((err) => this.sharedDataService.handleError(err))
    );
  }
  
  public getDealerDetailsResponse(response) {
    const dealerLocator = response?.showDealerLocatorDataArea?.dealerLocator?.[0];
    
    if (dealerLocator) {
        const dealerDetail = dealerLocator.dealerLocatorDetail?.[0];
        
        if (dealerDetail) {
            const dealerParty = dealerDetail.dealerParty?.specifiedOrganization;
            
            if (dealerParty) {
                this.updateDealerName(dealerParty);
                this.updateDealerPostalAddress(dealerParty);
                this.updateDealerContactInfo(dealerParty);
                this.updateDealerWebsite(dealerDetail, dealerParty);
                this.updateDealerBaseURL(dealerParty);
                this.updateDealerPoolInventory(dealerParty);
                if(dealerParty.divisionCode) {
                  this.checkIfSETDealer(dealerParty.divisionCode);
                  this.checkIfGSTDealer(dealerParty.divisionCode);
                }
            }
        }
    }
    
    this.sharedDataService.dealerAddress = this.constructDealerAddress();
}

private updateDealerPoolInventory(dealerParty) {
  const mainDealer = dealerParty?.primaryContact?.find( e => e?.departmentName?.value?.toLowerCase() === 'main dealer');
  if (mainDealer?.additionalDetailDescription) {
    const usedInventoryPooling = mainDealer?.additionalDetailDescription?.find(e => e?.title?.value?.toLowerCase() === 'Used Inventory Pooling'?.toLowerCase());
    const usedPooledDealers = usedInventoryPooling?.description?.map(x => x.value)?.filter(e => !_lodash.isEmpty(e));
    if (usedPooledDealers && usedPooledDealers?.length > 0) {
      this.sharedDataService.used_pooled_dealers = usedPooledDealers;
    }

    const newInventoryPooling = mainDealer?.additionalDetailDescription?.find(e => e?.title?.value?.toLowerCase() === 'Inventory Pooling'?.toLowerCase());
    const newPooledDealers = newInventoryPooling?.description?.map(x => x.value)?.filter(e => !_lodash.isEmpty(e));
    if (newPooledDealers && newPooledDealers?.length > 0) {
      this.sharedDataService.new_pooled_dealers = newPooledDealers;
    }
  }
}

private updateDealerName(dealerParty) {
    this.sharedDataService.dealerName = dealerParty.companyName?.value ?? ViewConstants.EMPTY_STRING;
}

private updateDealerPostalAddress(dealerParty) {
    this.sharedDataService.dealerPostalAddress = dealerParty.postalAddress;
    this.sharedDataService.dealerZipCode = dealerParty.postalAddress?.postcode?.value ?? ViewConstants.EMPTY_STRING;
    this.sharedDataService.dealerStateOrProvinceId = dealerParty.postalAddress?.stateOrProvinceCountrySubDivisionID?.value;
    this.sharedDataService.divisionCodeValue = dealerParty.divisionCode?.value ?? ViewConstants.EMPTY_STRING;
    this.sharedDataService.regionCode.next(this.sharedDataService.divisionCodeValue);
}

private updateDealerContactInfo(dealerParty) {
    if (dealerParty.primaryContact) {
        const phoneCommunication = dealerParty.primaryContact[0]?.telephoneCommunication?.[0];
        if (phoneCommunication?.channelCode?.value.toLowerCase() === ViewConstants.DEALER_PHONE_COLUMN.toLowerCase() && phoneCommunication?.completeNumber) {
            this.sharedDataService.dealerPhoneNo = phoneCommunication.completeNumber.value ?? ViewConstants.EMPTY_STRING;
        }
        const emailUriCommunication = dealerParty.primaryContact[0]?.uricommunication?.find(communication => communication?.channelCode?.value === ViewConstants.Email);
        if (emailUriCommunication && emailUriCommunication.uriid?.value) {
            this.sharedDataService.dealerEmail = emailUriCommunication.uriid.value;
        }
    }
}

private updateDealerWebsite(dealerDetail, dealerParty) {
    const uriCommunication = dealerParty.primaryContact?.[0]?.uricommunication?.[0];
    if (uriCommunication?.channelCode?.value === ViewConstants.Website && uriCommunication?.uriid?.value) {
        this.sharedDataService.dealerWebsite = uriCommunication.uriid.value;
        const dealType = dealerDetail?.dealerParty?.relationshipTypeCode?.value;
        if (dealType) {
            this.sharedDataService.dealerType=dealType;
            this.sharedDataService.relationshipTypeCode = dealType || ViewConstants.EMPTY_STRING;
            this.sendBrand(this.sharedDataService.relationshipTypeCode);
            this.getShortenLinkForPP(dealType, uriCommunication.uriid.value).subscribe(resp => {
                this.sharedDataService.shortenUrlForPP = resp?.shortenUrl;
            });
        }
    }
}

private updateDealerBaseURL(dealerParty) {
    if (Array.isArray(dealerParty.primaryContact)) {
        dealerParty.primaryContact.forEach(contact => {
            if (contact?.departmentName?.value === ViewConstants.Main_Dealer) {
                const websiteUriCommunication = contact.uricommunication?.find(data => data?.channelCode?.value === ViewConstants.Website);
                if (websiteUriCommunication) {
                    this.sharedDataService.dealerbaseUrl = websiteUriCommunication.uriid?.value;
                }
            }
        });
    }
}


  constructDealerAddress() {
    // Method to construct the Address of the Dealer
    const dealerPostalAddress = this.sharedDataService.dealerPostalAddress;
    const dealerPhoneNo = this.sharedDataService.dealerPhoneNo;
    if (dealerPostalAddress) {
        const lineOne = dealerPostalAddress.lineOne?.value || ViewConstants.EMPTY_STRING;
        const cityName = dealerPostalAddress.cityName?.value || ViewConstants.EMPTY_STRING;
        const stateOrProvince = dealerPostalAddress.stateOrProvinceCountrySubDivisionID?.value || ViewConstants.EMPTY_STRING;
        const countryID = dealerPostalAddress.countryID || ViewConstants.EMPTY_STRING;
        const postcode = dealerPostalAddress.postcode?.value || ViewConstants.EMPTY_STRING;
        
        let dealerAddress = `${lineOne}, ${cityName}, ${stateOrProvince}, ${countryID}, ${postcode}`;
        
        // Append phone number if available
        if (dealerPhoneNo) {
            dealerAddress += `   ${dealerPhoneNo}`;
        }
        
        return dealerAddress;
    }
    return ViewConstants.EMPTY_STRING;
}

  private brandObs$: BehaviorSubject<string> = new BehaviorSubject(null);
  sendBrand(message: string) {
    this.brandObs$.next(message);
  }
  getBrand(): Observable<any> {
    return this.brandObs$.asObservable();
  }

  checkIfGSTDealer(divisionCode: any): void {
    if (divisionCode?.value === "60") {
      this.sharedDataService.gstDealer = true;
    } else {
      this.sharedDataService.gstDealer = false;
    }
  }

  checkIfSETDealer(divisionCode: any): void {
    if (divisionCode?.value === "50") {
      this.sharedDataService.setDealer = true;
    } else {
      this.sharedDataService.setDealer = false;
    }
  }
}
